import { setPasswordApi } from "@/api/services/main/auth";
import { IconGoogle } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Linkable } from "@/components/elements/Linkable";
import { registerSchema } from "@/features/auth/schema/registerSchema";
import { TRegisterForm } from "@/features/auth/types/registerForm";
import { TRegisterQuery } from "@/features/auth/types/registerQuery";
import { buildCognitoGoogleLoginUrl } from "@/features/auth/utils/buildCognitoGoogleLoginUrl";
import { encodeCognitoAuthState } from "@/features/auth/utils/cognitoAuthState";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { logout } from "@/utils/logout";
import { getQueryFromURL } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Divider, Paper, Stack, Typography, styled } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

const initForm: TRegisterForm = {
  email: "",
  externalId: "",
  newPassword: "",
  confirmNewPassword: "",
};

export const RegisterForm = () => {
  const query = getQueryFromURL<TRegisterQuery>();
  const navigate = useNavigate();

  const [agree, setAgree] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const methods = useForm<TRegisterForm>({
    mode: "onChange",
    defaultValues: { ...initForm },
    resolver: yupResolver(registerSchema),
  });
  const { control } = methods;

  useEffect(() => {
    const email = query?.email || "";
    const externalId = query?.externalId || "";
    if (email) methods.setValue("email", email);
    if (externalId) methods.setValue("externalId", externalId);
  }, [query?.email, query?.externalId]);

  const onSubmit = async (data: TRegisterForm) => {
    const { newPassword } = data;
    if (!query) return;
    const { email, resetToken, externalId } = query;
    try {
      if (email && resetToken) {
        await setPasswordApi({ email, newPassword, resetToken, externalId });
        await logout();
        navigate(AppRoutes.registerComplete, { state: { email, externalId } });
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const handleGoogleLogin = () => {
    setIsGoogleLoading(true);
    // Carry the register context through the OAuth `state` param instead of web storage;
    // Cognito echoes it back to the AuthCallbackCognito page.
    const state = encodeCognitoAuthState({
      externalId: query?.externalId,
      email: query?.email,
      registerResetToken: query?.resetToken,
    });
    window.location.href = buildCognitoGoogleLoginUrl(state);
  };

  return (
    <Container>
      <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={registerSchema}>
        <Stack gap={3} width={400}>
          <Typography variant="head20Bold" textAlign="center" color={text.primary}>
            新規アカウント登録
          </Typography>

          <Stack flexDirection="column" gap="32px">
            <Stack flexDirection="column" gap="24px">
              <FormField control={control} name="externalId">
                <Input size="lg" readonly placeholder="R0123456789" />
              </FormField>
              <FormField control={control} name="email">
                <Input type="email" size="lg" readonly placeholder="example@iro-hana.com" />
              </FormField>
              <Stack gap="32px">
                <FormField control={control} name="newPassword">
                  <Input type="password" size="lg" />
                </FormField>
                <Stack flexDirection="column" gap="12px">
                  <FormField control={control} name="confirmNewPassword">
                    <Input type="password" size="lg" />
                  </FormField>
                  <Typography variant="cap12" color={text.secondary}>
                    ※パスワードはアルファベット大文字・小文字・数字・記号を含む12文字以上で入力してください。
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            <Stack flexDirection="column" gap={2}>
              <Checkbox
                checked={agree}
                label={
                  <Stack direction="row">
                    <Linkable href="https://iro-hana.com/privacy/" color={text.link} target="_blank">
                      <Typography variant="body14" sx={{ textDecoration: "underline" }}>
                        プライバシーポリシー
                      </Typography>
                    </Linkable>
                    に同意します。
                  </Stack>
                }
                onChange={(e) => setAgree(e.target.checked)}
              />
              <Button type="submit" size="sm" variant="primary" fullWidth disabled={!agree}>
                <Typography variant="body14Bold">会員登録する</Typography>
              </Button>
              <Stack gap={2}>
                <Divider>
                  <Typography variant="cap12" color={text.secondary}>
                    または
                  </Typography>
                </Divider>
                <Button size="sm" variant="outline" fullWidth onClick={handleGoogleLogin} disabled={!agree} isLoading={isGoogleLoading}>
                  <Stack direction="row" gap={1} alignItems="center">
                    <IconGoogle fontSize={20} style={{ filter: !agree || isGoogleLoading ? "grayscale(100%)" : "none" }} />
                    <Typography variant="body14Semi">Googleで登録</Typography>
                  </Stack>
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Form>
    </Container>
  );
};

const Container = styled(Paper)`
  border-radius: 12px;
  padding: 24px;
  box-shadow:
    0 4px 4px -1px rgba(12, 12, 13, 0.05),
    0 4px 4px -1px rgba(12, 12, 13, 0.1) !important;
`;
